import {
  ApolloClient,
  ApolloLink,
  FieldPolicy,
  InMemoryCache,
} from '@apollo/client';
import '@apollo/client';
import { LocalState } from '@apollo/client/local-state';

import { API_BASE_URL } from '@root/config/constants';

import { buildErrorLink } from './build_error_link';
import { buildHttpLink } from './build_http_link';
import { dedupeAvailabilityWindows } from './dedupe_by';
import stringify from 'fast-json-stable-stringify';

/** Control + product pages: match `origin/master` cache behavior (range merge). */
export type PlatformAvailabilityCacheMode = 'legacy' | 'checkoutV2';

export type BuildPlatformApolloClientOptions = {
  /** Default `legacy` (master). `/checkout/steps/*` uses `checkoutV2`. */
  availabilityCacheMode?: PlatformAvailabilityCacheMode;
};

/**
 * Keyed by `input.order`, merge widened ranges. Dedupe by full window, not only `fromTime`, so
 * flexible all-day rows are not collapsed with priority slots that share the same start instant.
 */
const LEGACY_AVAILABILITIES_FIELD_POLICY: FieldPolicy = {
  keyArgs: (args) =>
    stringify((args?.input as { order?: unknown } | undefined)?.order ?? {}),
  merge(existing, incoming) {
    if (!existing) return incoming;
    return dedupeAvailabilityWindows([...existing, ...incoming]);
  },
};

/** Same as master `LONG_DISTANCE_PICKUP_AVAILABILITY_FIELD_POLICY`. */
const LEGACY_LONG_DISTANCE_PICKUP_FIELD_POLICY: FieldPolicy = {
  keyArgs: (args) => stringify(args?.zip ?? ''),
  merge(existing, incoming) {
    if (!existing) {
      return incoming;
    }
    return dedupeAvailabilityWindows([...existing, ...incoming]);
  },
};

/**
 * Same intent as master `LONG_DISTANCE_DELIVERY_AVAILABILITY_FIELD_POLICY`.
 * Apollo 4 merge options expose operation variables as `variables` (not `args.variables`).
 */
const LEGACY_LONG_DISTANCE_DELIVERY_FIELD_POLICY: FieldPolicy = {
  keyArgs: (args) => stringify(args?.zip ?? ''),
  merge(existing, incoming, { variables }) {
    if (!existing) {
      return incoming;
    }
    if (variables !== undefined && variables.from === variables.pickupDate) {
      return incoming;
    }
    return dedupeAvailabilityWindows([...existing, ...incoming]);
  },
};

/** Checkout V2: full query keys, no merge (per-month / explicit prefetch). */
const CHECKOUT_V2_AVAILABILITIES_FIELD_POLICY: FieldPolicy = {
  keyArgs: (args) => stringify(args?.input ?? {}),
};

const CHECKOUT_V2_LONG_DISTANCE_PICKUP_FIELD_POLICY: FieldPolicy = {
  keyArgs: (args) =>
    stringify({
      zip: args?.zip,
      from: args?.from,
      till: args?.till,
      movingQuoteSignedID: args?.movingQuoteSignedID,
    }),
};

const CHECKOUT_V2_LONG_DISTANCE_DELIVERY_FIELD_POLICY: FieldPolicy = {
  keyArgs: (args) =>
    stringify({
      zip: args?.zip,
      from: args?.from,
      till: args?.till,
      pickupDate: args?.pickupDate,
      movingQuoteSignedID: args?.movingQuoteSignedID,
    }),
};

function availabilityTypePolicies(mode: PlatformAvailabilityCacheMode) {
  if (mode === 'checkoutV2') {
    return {
      availabilities: CHECKOUT_V2_AVAILABILITIES_FIELD_POLICY,
      longDistancePickupAvailabilities:
        CHECKOUT_V2_LONG_DISTANCE_PICKUP_FIELD_POLICY,
      longDistanceDeliveryAvailabilities:
        CHECKOUT_V2_LONG_DISTANCE_DELIVERY_FIELD_POLICY,
    };
  }
  return {
    availabilities: LEGACY_AVAILABILITIES_FIELD_POLICY,
    longDistancePickupAvailabilities: LEGACY_LONG_DISTANCE_PICKUP_FIELD_POLICY,
    longDistanceDeliveryAvailabilities:
      LEGACY_LONG_DISTANCE_DELIVERY_FIELD_POLICY,
  };
}

export const buildPlatformApolloClient = (
  options: BuildPlatformApolloClientOptions = {},
) => {
  const mode = options.availabilityCacheMode ?? 'legacy';

  return new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: availabilityTypePolicies(mode),
        },
      },
    }),

    link: ApolloLink.from([
      buildErrorLink(),
      buildHttpLink(`${API_BASE_URL}/graphql`),
    ]),
    localState: new LocalState({}),
  });
};
